import { API_BASE_URL } from '@/app/config/apiConfig';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  clearAuthTokens,
  getAuthRole,
  getAuthRoles,
  getAuthStatus,
  getAuthToken,
  getAuthUid,
  saveAuthTokens,
  shouldRefreshAuthToken,
} from '@/services/auth';
import { AuthApi } from '@/services/auth';

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * 验证 user_id 是否为有效的正整数
 * 后端 API 要求 user_id 必须为正整数，否则返回 422
 */
export function validatePositiveIntegerUserId(userId: number, context: string): void {
  if (!Number.isFinite(userId) || !Number.isInteger(userId) || userId <= 0) {
    throw new Error(`${context}: user_id 必须为正整数，当前值: ${userId}`);
  }
}

// GET 请求去重：同一 URL 的并发 GET 共享同一个 Promise，避免重复网络请求
const inflightGetRequests = new Map<string, Promise<unknown>>();

// Token 刷新的 Promise 锁，防止并发刷新
let refreshTokenPromise: Promise<void> | null = null;

function dispatchAuthSessionExpired(): void {
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
}

function requireCurrentAuth(): { token: string; uid: number } {
  const currentToken = getAuthToken();
  const currentUid = getAuthUid();

  if (!currentToken || currentUid === null) {
    clearAuthTokens();
    dispatchAuthSessionExpired();
    throw new Error('未授权：请重新登录');
  }

  return { token: currentToken, uid: currentUid };
}

async function refreshCurrentToken(
  currentToken: string,
  currentUid: number,
  logoutOnFailure: boolean,
): Promise<void> {
  if (refreshTokenPromise) {
    await refreshTokenPromise;
    return;
  }

  refreshTokenPromise = (async () => {
    try {
      const refreshResponse = await AuthApi.refreshToken(currentToken, currentUid);
      saveAuthTokens(
        refreshResponse.token,
        currentUid,
        refreshResponse.expires_in,
        getAuthRole(),
        getAuthRoles(),
        getAuthStatus() ?? undefined,
      );
    } catch (error) {
      if (logoutOnFailure) {
        clearAuthTokens();
        dispatchAuthSessionExpired();
      }
      throw error;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  await refreshTokenPromise;
}

async function proactivelyRefreshCurrentToken(): Promise<void> {
  if (!shouldRefreshAuthToken()) return;

  const currentToken = getAuthToken();
  const currentUid = getAuthUid();
  if (!currentToken || currentUid === null) return;

  try {
    await refreshCurrentToken(currentToken, currentUid, false);
  } catch {
    // Token 尚未被服务端拒绝时保留现有会话，由后续 401 路径决定是否退出。
  }
}

export async function request<T>(
  path: string,
  init?: RequestInit,
  _isRetry: boolean = false,
): Promise<T> {
  const isGetRequest = !init?.method || init.method.toUpperCase() === 'GET';
  if (isGetRequest && !_isRetry) {
    const dedupeKey = `${API_BASE_URL}${path}`;
    const inflight = inflightGetRequests.get(dedupeKey);
    if (inflight) {
      return inflight as Promise<T>;
    }
    const requestPromise = executeRequest<T>(path, init, _isRetry).finally(() => {
      inflightGetRequests.delete(dedupeKey);
    });
    inflightGetRequests.set(dedupeKey, requestPromise);
    return requestPromise;
  }
  return executeRequest<T>(path, init, _isRetry);
}

async function executeRequest<T>(
  path: string,
  init?: RequestInit,
  _isRetry: boolean = false,
): Promise<T> {
  const isFormData = init?.body instanceof FormData;

  if (!_isRetry) {
    await proactivelyRefreshCurrentToken();
  }

  // 从 Cookie 读取认证信息
  const token = getAuthToken();
  const uid = getAuthUid();

  // 构建请求头，优先注入认证头，允许用户传入的 headers 覆盖
  const authHeaders: Record<string, string> = {};
  if (token) {
    authHeaders['Authorization'] = `Bearer ${token}`;
  }
  if (uid) {
    authHeaders['X-User-Uid'] = String(uid);
  }

  const headers = isFormData
    ? {
        ...authHeaders,
        ...(init?.headers ?? {}),
      }
    : {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...(init?.headers ?? {}),
      };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    // 处理 401 未授权错误
    if (response.status === 401 && !_isRetry) {
      const { token: currentToken, uid: currentUid } = requireCurrentAuth();

      // 有 token，尝试刷新
      try {
        await refreshCurrentToken(currentToken, currentUid, true);

        // 刷新成功，用新 token 重试原请求（只重试一次）
        return request<T>(path, init, true);
      } catch {
        throw new Error('认证刷新失败，请重新登录');
      }
    }

    // 非 401 错误或重试后的 401 错误，走原有错误处理逻辑
    let message = '请求失败';
    let errorBody: unknown = null;
    try {
      const body = await response.json();
      errorBody = body;
      const detail = body.message ?? body.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        const first = detail[0];
        if (typeof first === 'string') {
          message = first;
        } else if (first && typeof first === 'object') {
          message =
            (typeof first.msg === 'string' && first.msg) ||
            (Array.isArray(first.loc) && first.loc.length > 0
              ? `${first.loc.join('.')} ${first.msg ?? ''}`.trim()
              : '') ||
            JSON.stringify(first);
        }
      } else if (detail && typeof detail === 'object') {
        message = JSON.stringify(detail);
      }
    } catch {
      // ignore parse errors
    }
    throw new ApiError(message, response.status, errorBody);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export interface UploadProgressOptions {
  onProgress?: (percent: number) => void;
  timeoutMs?: number;
  signal?: AbortSignal;
  /**
   * 在 xhr.send 前回调，把内部 XMLHttpRequest 实例暴露给调用方。
   * 全局上传中心据此实现「取消」操作（直接 xhr.abort()），无需 AbortController。
   */
  registerXhr?: (xhr: XMLHttpRequest) => void;
}

/**
 * 基于 XMLHttpRequest 的文件上传，支持上传进度回调和超时控制。
 * 用于大文件上传场景（如 ZIP 批量上传），替代 fetch 无法获取上传进度的限制。
 */
export async function uploadWithProgress<T>(
  path: string,
  body: FormData,
  options?: UploadProgressOptions,
): Promise<T> {
  const { onProgress, timeoutMs = 10 * 60 * 1000, signal, registerXhr } = options ?? {};
  await proactivelyRefreshCurrentToken();

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}${path}`);
    xhr.timeout = timeoutMs;
    if (registerXhr) {
      registerXhr(xhr);
    }

    const token = getAuthToken();
    const uid = getAuthUid();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    if (uid) {
      xhr.setRequestHeader('X-User-Uid', String(uid));
    }

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          // 上限 99%：upload.progress 只反映客户端发送进度，
          // 服务端仍需解压/处理，真正完成由调用方在接口返回后设为 100%
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(Math.min(percent, 99));
        }
      });
    }

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('上传已取消'));
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          resolve(undefined as T);
        }
      } else {
        let message = '上传失败';
        try {
          const errorBody = JSON.parse(xhr.responseText);
          message = errorBody.message ?? errorBody.detail ?? message;
          if (typeof message !== 'string') {
            message = JSON.stringify(message);
          }
        } catch {
          // ignore parse errors
        }
        reject(new Error(message));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('网络错误，上传失败')));
    xhr.addEventListener('timeout', () => reject(new Error('上传超时，请检查网络后重试')));
    xhr.addEventListener('abort', () => reject(new Error('上传已取消')));

    xhr.send(body);
  });
}

export { API_BASE_URL };

/**
 * 获取资源 Blob，复用 request 的 401 自动刷新与重试逻辑。
 *
 * 与 request() 的区别：
 * - 返回原生 Response 对象，由调用方自行决定 .blob() / .text() / .json()
 * - 支持传入完整 URL（外部资源）或相对路径（内部 API），自动拼接 API_BASE_URL
 * - 自动携带认证头，并在 401 时尝试刷新 token 后重试一次
 *
 * @param url  完整 URL 或相对路径
 * @param init 可选 RequestInit
 */
export async function fetchResource(url: string, init?: RequestInit): Promise<Response> {
  const isFullUrl = /^https?:\/\//i.test(url);
  const targetUrl = isFullUrl ? url : `${API_BASE_URL}${url}`;
  await proactivelyRefreshCurrentToken();

  // 复用 executeRequest 的 401 处理：先尝试直接 fetch，401 则刷新后重试
  const doFetch = async (_isRetry: boolean): Promise<Response> => {
    const token = getAuthToken();
    const uid = getAuthUid();

    const authHeaders: Record<string, string> = {};
    if (token) {
      authHeaders['Authorization'] = `Bearer ${token}`;
    }
    if (uid) {
      authHeaders['X-User-Uid'] = String(uid);
    }

    const response = await fetch(targetUrl, {
      ...init,
      headers: {
        ...authHeaders,
        ...(init?.headers ?? {}),
      },
    });

    if (!response.ok && response.status === 401 && !_isRetry) {
      const { token: currentToken, uid: currentUid } = requireCurrentAuth();

      try {
        await refreshCurrentToken(currentToken, currentUid, true);
        return doFetch(true);
      } catch {
        throw new Error('认证刷新失败，请重新登录');
      }
    }

    return response;
  };

  return doFetch(false);
}

export interface DownloadProgressOptions {
  /**
   * 下载进度回调。
   * - loaded: 当前已接收字节数
   * - total : 响应总字节数；当服务端未返回 Content-Length 时为 undefined
   */
  onProgress?: (loaded: number, total: number | undefined) => void;
  /** 取消下载信号 */
  signal?: AbortSignal;
  /** 暴露内部 XHR 实例给调用方（与 uploadWithProgress 一致，便于自定义取消逻辑） */
  registerXhr?: (xhr: XMLHttpRequest) => void;
}

/**
 * 基于 XMLHttpRequest 的资源下载，支持下载进度回调与 401 自动刷新重试。
 *
 * 用于批量下载场景：原生 fetch 无法在浏览器侧获取下载进度（需要 ReadableStream 自行计数），
 * 而 XHR 的 progress 事件可直接给出 loaded/total 字节数，更易实现总进度。
 *
 * - 支持完整 URL（OSS/CDN 直链）或相对路径（自动拼 API_BASE_URL）
 * - 仅 API 地址注入鉴权头并处理 401；外部资源直链不携带内部会话头
 * - signal.abort() → xhr.abort()，reject('下载已取消')
 */
export async function downloadWithProgress(
  url: string,
  options?: DownloadProgressOptions,
): Promise<Blob> {
  const { onProgress, signal, registerXhr } = options ?? {};
  const isFullUrl = /^https?:\/\//i.test(url);
  const targetUrl = isFullUrl ? url : `${API_BASE_URL}${url}`;
  const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
  const shouldAttachAuth = !isFullUrl || new URL(targetUrl).origin === apiOrigin;
  if (shouldAttachAuth) await proactivelyRefreshCurrentToken();

  const send = (isRetry: boolean): Promise<Blob> =>
    new Promise<Blob>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', targetUrl);
      xhr.responseType = 'blob';
      if (registerXhr) {
        registerXhr(xhr);
      }

      if (shouldAttachAuth) {
        const token = getAuthToken();
        const uid = getAuthUid();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        if (uid) {
          xhr.setRequestHeader('X-User-Uid', String(uid));
        }
      }

      let abortHandler: (() => void) | null = null;
      if (signal) {
        if (signal.aborted) {
          xhr.abort();
          reject(new Error('下载已取消'));
          return;
        }
        abortHandler = () => {
          xhr.abort();
        };
        signal.addEventListener('abort', abortHandler);
      }
      const cleanupSignal = () => {
        if (signal && abortHandler) {
          signal.removeEventListener('abort', abortHandler);
        }
      };

      if (onProgress) {
        xhr.addEventListener('progress', (event) => {
          onProgress(event.loaded, event.lengthComputable ? event.total : undefined);
        });
      }

      xhr.addEventListener('load', () => {
        cleanupSignal();
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response as Blob;
          // 兜底：若服务端没有 Content-Length，progress 不触发 total，下载完成后用 blob.size 兜底
          if (onProgress) {
            onProgress(blob.size, blob.size);
          }
          resolve(blob);
          return;
        }
        if (xhr.status === 401 && shouldAttachAuth && !isRetry) {
          // 401：尝试刷新 token 后重试一次
          let currentAuth: { token: string; uid: number };
          try {
            currentAuth = requireCurrentAuth();
          } catch (error) {
            reject(error);
            return;
          }
          (async () => {
            try {
              await refreshCurrentToken(currentAuth.token, currentAuth.uid, true);
              const retried = await send(true);
              resolve(retried);
            } catch {
              reject(new Error('认证刷新失败，请重新登录'));
            }
          })();
          return;
        }
        reject(new Error(`下载失败：HTTP ${xhr.status}`));
      });

      xhr.addEventListener('error', () => {
        cleanupSignal();
        reject(new Error('网络错误，下载失败'));
      });
      xhr.addEventListener('abort', () => {
        cleanupSignal();
        reject(new Error('下载已取消'));
      });

      xhr.send();
    });

  return send(false);
}
